import { createApp, h } from 'vue';
import { Extension } from '@tiptap/core';
import { Plugin, EditorState, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import {
  ySyncPluginKey,
  absolutePositionToRelativePosition,
  relativePositionToAbsolutePosition,
} from 'y-prosemirror';
import { recreateTransform } from '@fellow/prosemirror-recreate-transform';
import PhantomSuggesterComponent from './PhantomSuggester.vue';
import PhantomSuggesterKey from './pluginKey';
import userMentionSuggestion from '../../services/lib/userMentionSuggestion';
import commandSuggestion from '../../services/lib/commandSuggestion';
import emojiSuggestion from '../../services/lib/emojiSuggestion.js';
import { ResourceType } from '@/types/resources';
import EditorService from '@/components/editor/services/EditorService';
import isSafari from '@/utilities/isSafari';
import isFirefox from '@/utilities/isFirefox';
import tryOpenSuggester from './tryOpenSuggester';

type PhantomTrPayload =
  | {
      type: 'add';
      pos: number;
    }
  | {
      type: 'remove';
    }
  | {
      type: 'update';
      query: string;
    };

type PhantomState = {
  decoration: DecorationSet;
  pos: number | null;
  yjsPos: Record<string, any> | null;
  query: string;
};

type Renderer = ReturnType<(typeof userMentionSuggestion)['render']>;

type DecorationBuilderProps = {
  state: EditorState;
  prefix: string;
  position?: Record<string, any> | number;
  query?: string;
  key: string | PluginKey;
  editor: EditorService;
};

export const createPhantomDecoration = ({
  state,
  prefix,
  position,
  query,
  key,
  editor,
}: DecorationBuilderProps) => {
  const YState = ySyncPluginKey.getState(state);

  if (typeof position === 'undefined') return DecorationSet.empty;

  let resolvedPos: Record<string, any> | number | null;

  if (typeof position === 'number') {
    /**
     * If the position is absolute, do not perform any mapping.
     */
    resolvedPos = position;
  } else {
    /**
     * Otherwise, convert to absolute.
     */
    resolvedPos = relativePositionToAbsolutePosition(
      YState.doc,
      YState.type,
      position,
      YState.binding.mapping
    );
  }

  if (resolvedPos === null || Number.isNaN(position)) return DecorationSet.empty;

  /**
   * It has to be at least 1, otherwise it
   * will get inserted before the paragraph.
   */
  resolvedPos = Math.max(1, Number(resolvedPos));

  const decoration = Decoration.widget(
    resolvedPos,
    (view, getPos) => {
      const container = document.createElement('typeahead');
      const widget = document.createElement('typeahead');
      const widget2 = document.createElement('typeahead');
      widget.textContent = prefix;
      widget.id = 'typeahead';
      widget.classList.add('text-blue-600');

      widget.addEventListener('keydown', (e) => e.stopPropagation(), true);

      /**
       *? This setTimeout is used for delaying the mounting step.
       *? Vue requires a real DOM element to be mounted onto.
       *? At this stage, the element is just a node which is not
       *? part of the DOM.
       *? Queueing this onto the event loop allows the browser to
       *? return this function and insert the element into the DOM,
       *? effectively allowing Vue to mount onto a real element.
       *?
       *? The timeout amount is not important, but setting it to
       *? 0ms might be a bit buggy on some browsers.
       */
      setTimeout(() => {
        const vNode = createApp({
          render: () =>
            h(PhantomSuggesterComponent, {
              view,
              getPos,
              query: query ?? '',
              pluginKey: key,
              prefix,
              storeDispatch: editor.storeDispatch,
            }),
        });

        // @ts-ignore
        vNode.appContext = window.StVueApp?._context;
        vNode.mount(widget2);
      }, 5);

      container.appendChild(widget);
      container.appendChild(widget2);

      return container;
    },
    { side: -1, ignoreSelection: true }
  );

  return DecorationSet.create(state.doc, [decoration]);
};

export type PhantomPluginProps = {
  editor: EditorService;
  renderer: Renderer;
  prefix: string;
  key: PluginKey;
  suggestion: any;
  /**
   * The keys are required in order to check for conflicting behaviour.
   * In case conflicts between suggesters need to be handled, these
   * identifiers allow individual suggester inspection.
   */
  suggesterKeys: PluginKey[];
  resourceType: ResourceType;
  /**
   * Define what preceding characters are permitted for starting a
   * suggester.
   * @default /[\s()\[\]"':‘’“”]+/
   */
  allowedPreceding?: RegExp;
  /**
   * Node names that cannot be parents of suggesters. For example,
   * to deny suggesters in codeBlocks, set this to: `['codeBlock']`.
   */
  parentDenylist?: string[];
};

const PhantomPlugin = ({
  editor,
  renderer,
  prefix,
  key,
  suggestion,
  suggesterKeys,
  resourceType,
  allowedPreceding,
  parentDenylist,
}: PhantomPluginProps) =>
  /**
   * TODO: Optimize to not run (at all if possible) when other
   * suggesters are active. This can be checked by some state variables.
   *
   * This will remove the need of checking for other suggesters when performing
   * tasks such as adding the placeholder override. It should also improve
   * performance, since every suggester is ran on every transaction.
   */
  new Plugin<PhantomState>({
    key,
    state: {
      init() {
        return {
          decoration: DecorationSet.empty,
          pos: null,
          yjsPos: null,
          query: '',
        };
      },
      apply(tr, stateValue, oldState, newState) {
        const action: PhantomTrPayload | null = tr.getMeta(key);

        const YState = ySyncPluginKey.getState(newState);

        const collabEnabled = !!YState;

        /**
         * Props are exposed as a function in order to take
         * advantage of their lazy behavior.
         * @param newQuery - Optional, in cases where the stateValue
         * is obsolete/hasn't been updated yet.
         */
        const props = (newQuery?: string) => ({
          editor,
          items: suggestion.items({ query: newQuery ?? stateValue.query, resourceType }),
          command: (commandProps: any) => {
            const content =
              typeof commandProps === 'string'
                ? commandProps
                : [
                    {
                      type: commandProps.nodeName,
                      attrs: commandProps,
                    },
                    {
                      type: 'text',
                      text: ' ',
                    },
                  ];
            editor
              .chain()
              .insertContent(content)
              .setMeta(key as unknown as string, { type: 'remove' })
              .focus()
              .run();
          },
          clientRect: () => {
            const rect = document.querySelector('typeahead')?.getBoundingClientRect();
            /**
             * If rect is null, that means that the decoration was unsuccessfully rendered.
             * To avoid hidden key captures, we remove the suggester altogether.
             */
            if (!rect) {
              editor
                .chain()
                .setMeta(key as unknown as string, { type: 'remove' })
                .run();
              return null;
            }
            return rect;
          },
          resourceType,
        });

        switch (action?.type) {
          // Suggester addition
          case 'add':
            editor.view.dom.classList.add('force-placeholder-off');
            renderer.onStart(props());
            if (collabEnabled) {
              const pos = absolutePositionToRelativePosition(
                action.pos,
                YState.type,
                YState.binding.mapping
              );
              return {
                ...stateValue,
                decoration: createPhantomDecoration({
                  state: newState,
                  position: pos,
                  prefix,
                  key,
                  editor,
                }),
                yjsPos: pos,
              };
            }
            return {
              ...stateValue,
              decoration: createPhantomDecoration({
                state: newState,
                position: action.pos,
                prefix,
                key,
                editor,
              }),
              pos: action.pos,
            };
          // Suggester removal
          case 'remove':
            editor.view.dom.classList.remove('force-placeholder-off');
            renderer.onExit();
            return {
              decoration: DecorationSet.empty,
              pos: null,
              yjsPos: null,
              query: '',
            };
          case 'update':
            editor.view.dom.classList.add('force-placeholder-off');
            renderer.onUpdate(props(action.query));
            return {
              ...stateValue,
              query: action.query,
            };
          // All other transactions
          default: {
            /**
             * The extracted numerical value
             * representing the location of the decoration.
             *
             *! This is out of sync. Any match between this
             *! and the real position is situational, and should not
             *! be relied upon.
             */
            const decorationPos = stateValue.decoration.find()[0]?.from;

            const posValid = collabEnabled ? stateValue.yjsPos : stateValue.pos !== null;

            /**
             * If there is no decoration present on the screen,
             * or the relative position is unavailable, clear
             * the state, effectively removing the decoration.
             */
            if (!posValid || typeof decorationPos !== 'number') {
              /**
               * If other suggesters are active, do not remove the placeholder
               * override class.
               */
              let otherSuggesterActive = false;

              for (const suggesterKey of suggesterKeys) {
                if (suggesterKey.getState(editor.state).decoration !== DecorationSet.empty) {
                  otherSuggesterActive = true;
                  break;
                }
              }

              if (!otherSuggesterActive) {
                editor.view.dom.classList.remove('force-placeholder-off');
              }

              return {
                decoration: DecorationSet.empty,
                pos: null,
                yjsPos: null,
                query: '',
              };
            }

            if (!collabEnabled) {
              editor.view.dom.classList.add('force-placeholder-off');
              return {
                ...stateValue,
                decoration: stateValue.decoration.map(tr.mapping, tr.doc),
              };
            }

            /**
             * If the change originated from the local client,
             * it means that the decoration can be mapped.
             *
             * This saves a re-render.
             *
             *? Normally this plugin should have the user focused
             *? on the suggesting field. Still, this is a good
             *? optimization to have.
             */
            if (!YState.isChangeOrigin) {
              editor.view.dom.classList.add('force-placeholder-off');
              return {
                ...stateValue,
                decoration: stateValue.decoration.map(tr.mapping, tr.doc),
              };
            }

            /**
             * This is the absolute position, according to YJS.
             *
             *! This is the valid, fresh position of where the
             *! decoration should be.
             */
            const absPos = relativePositionToAbsolutePosition(
              YState.doc,
              YState.type,
              stateValue.yjsPos,
              YState.binding.mapping
            );

            /**
             * If mapping failed, or the position became unavailable,
             * clear the state.
             */
            if (!absPos) {
              editor.view.dom.classList.remove('force-placeholder-off');
              return {
                decoration: DecorationSet.empty,
                pos: null,
                yjsPos: null,
                query: '',
              };
            }

            /**
             * In cases where a YJS change occured, but the
             * numerical position has not changed, we do not need
             * to remap the decoration.
             *
             */
            if (decorationPos === absPos) {
              editor.view.dom.classList.add('force-placeholder-off');
              return { ...stateValue };
            }

            /**
             * If the numerical position did, in fact,
             * change, the YJS has removed the decoration.
             *
             * As a solution, we recreate the steps and
             * map the decoration.
             *
             *? The "old" stateValue.yjsPos position is still
             *? valid, as it is being stored as a relative
             *? YJS position.
             */
            editor.view.dom.classList.add('force-placeholder-off');
            renderer.onUpdate(props());

            const mapping = recreateTransform(tr.before, tr.doc, {
              complexSteps: true,
              wordDiffs: false,
              simplifyDiff: true,
            }).mapping;

            return {
              ...stateValue,
              decoration: stateValue.decoration.map(mapping, tr.doc),
            };
          }
        }
      },
    },
    props: {
      decorations(state) {
        return key.getState(state).decoration || DecorationSet.empty;
      },
      handleTextInput(view, from, to, text) {
        const wasHandled = tryOpenSuggester({
          view,
          from,
          to,
          typedChar: text,
          prefix,
          pluginKey: key,
          parentDenylist,
          allowedPreceding,
        });
        return wasHandled;
      },
      /**
       * All keyboard events should be captured within
       * the rendered Vue component. On mobile, however (more specifically
       * Chrome with GBoard), some composite keyboard events cannot be captured
       * and stopped, thus "leaking" into ProseMirror.
       *
       * This is where they are handled. While the code is not specifically set
       * up to only run on mobile, these situations are observed to only
       * occur in that environment.
       */
      handleKeyDown(view, event) {
        const YState = ySyncPluginKey.getState(view.state);

        const collabEnabled = !!YState;

        const posValid = collabEnabled
          ? key.getState(view.state).yjsPos
          : key.getState(view.state).pos !== null;

        /**
         * If the suggester is active...
         */
        if (posValid) {
          /**
           * ... and one of these keypresses escaped the suggester...
           */
          if (['Backspace', 'Enter', 'Space'].includes(event.code)) {
            /**
             * ... dispatch a removal transaction.
             */
            const { tr } = view.state;
            tr.setMeta(key, {
              type: 'remove',
            });
            view.dispatch(tr);
            return true;
          }
        } else if (event.key === prefix && (isSafari() || isFirefox())) {
          const { from, to } = view.state.selection;
          const wasHandled = tryOpenSuggester({
            view,
            from,
            to,
            typedChar: event.key,
            prefix,
            pluginKey: key,
            parentDenylist,
            allowedPreceding,
          });

          if (wasHandled) {
            return true;
          }
        }
        return false;
      },
    },
  });

const TipTapPhantomPlugin = Extension.create({
  addOptions(): { resourceType?: ResourceType } {
    return { resourceType: undefined };
  },
  addProseMirrorPlugins() {
    const renderer = userMentionSuggestion.render?.();
    const emojiRenderer = emojiSuggestion.render?.();
    const commandRenderer = commandSuggestion.render?.();
    const allKeys = Object.values(PhantomSuggesterKey);
    const { resourceType } = this.options;
    return [
      PhantomPlugin({
        editor: this.editor as any,
        renderer,
        prefix: '@',
        key: PhantomSuggesterKey.mention,
        suggestion: userMentionSuggestion,
        suggesterKeys: allKeys,
        resourceType,
        parentDenylist: ['codeBlock'],
      }),
      PhantomPlugin({
        editor: this.editor as any,
        renderer: emojiRenderer,
        prefix: ':',
        key: PhantomSuggesterKey.emoji,
        suggestion: emojiSuggestion,
        suggesterKeys: allKeys,
        resourceType,
        allowedPreceding: /[\s([\]"':‘’“”]+/,
      }),
      PhantomPlugin({
        editor: this.editor as any,
        renderer: commandRenderer,
        prefix: '/',
        key: PhantomSuggesterKey.command,
        suggestion: commandSuggestion,
        suggesterKeys: allKeys,
        resourceType,
        parentDenylist: ['codeBlock'],
      }),
    ];
  },
});

export { TipTapPhantomPlugin as PhantomPlugin, PhantomPlugin as PmPhantomPlugin };
